import { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { SideModal } from "../../../components/SideModal";
import Button from "../../../components/Button";
import { ProvisioningUser } from "../types";
import { OverallStatusBadge } from "./StatusBadge";
import { VerticalStepper } from "./VerticalStepper";
import { ResourceCards } from "./ResourceCards";
import { BrandAssets } from "../../../components/BrandAssets";
import { CompetitorStepEditor } from "../../../components/CompetitorStepEditor";
import { useConfirmTracking } from "../hooks/useOnboardingTracking";
import { ConfirmTrackingEmailPreviewModal } from "./ConfirmTrackingEmailPreviewModal";

type Tab = "pipeline" | "brand-assets" | "competitors";

function TabButton({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-sm py-2 font-medium -mb-px border-b-2 ${
        isActive
          ? "border-brand-primary text-content-primary"
          : "border-transparent text-content-secondary hover:text-content-primary"
      }`}
    >
      {children}
    </button>
  );
}

function SetupModeBadge({
  setupMode,
}: {
  setupMode: ProvisioningUser["setupMode"];
}) {
  if (setupMode === "self_serve") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
        Self-Serve
      </span>
    );
  }
  if (setupMode === "concierge") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">
        Concierge
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-neutral-100 text-content-secondary">
      Legacy
    </span>
  );
}

function PathDetail({ user }: { user: ProvisioningUser }) {
  if (user.setupMode === "concierge") {
    const owner = user.conciergeOwner;
    return (
      <div className="rounded-md border border-border-primary px-4 py-3 text-sm">
        <div className="text-xs font-medium text-content-secondary uppercase mb-1">
          Concierge owner
        </div>
        {owner ? (
          <div className="text-content-primary">
            {owner.name}
            {owner.slackHandle && (
              <span className="ml-2 text-content-secondary">
                {owner.slackHandle}
              </span>
            )}
          </div>
        ) : (
          <div className="text-content-secondary italic">Unassigned</div>
        )}
      </div>
    );
  }

  if (user.setupMode === "self_serve") {
    const v = user.selfServeVerificationResults;
    const verifiedAt = user.selfServeVerifiedAt
      ? moment(user.selfServeVerifiedAt)
      : null;
    return (
      <div className="rounded-md border border-border-primary px-4 py-3 text-sm space-y-2">
        <div>
          <div className="text-xs font-medium text-content-secondary uppercase mb-1">
            Tag verification
          </div>
          {verifiedAt ? (
            <div className="text-emerald-700">
              Verified {verifiedAt.fromNow()}
            </div>
          ) : v?.overallStatus === "failed" ? (
            <div className="text-red-700">
              Last run failed
              {v.skippedByCustomer && " (customer skipped)"}
            </div>
          ) : v?.overallStatus === "running" ? (
            <div className="text-blue-700">Run in progress</div>
          ) : (
            <div className="text-content-secondary italic">
              Not yet verified
            </div>
          )}
        </div>
        {user.pmsBookingEngineUrl && (
          <div>
            <div className="text-xs font-medium text-content-secondary uppercase mb-1">
              Booking engine
            </div>
            <a
              href={user.pmsBookingEngineUrl}
              target="_blank"
              rel="noreferrer"
              className="text-brand-primary underline break-all"
            >
              {user.pmsBookingEngineUrl}
            </a>
          </div>
        )}
      </div>
    );
  }

  return null;
}

function ActionsCard({ user }: { user: ProvisioningUser }) {
  const confirmTracking = useConfirmTracking();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const alreadyConfirmed = user.selfServeVerifiedAt
    ? moment(user.selfServeVerifiedAt)
    : null;

  const sendAndConfirm = () => {
    confirmTracking.mutate(user.userId, {
      onSuccess: () => setIsPreviewOpen(false),
    });
  };

  return (
    <div className="rounded-md border border-border-primary px-4 py-3 text-sm">
      <div className="text-xs font-medium text-content-secondary uppercase mb-2">
        Actions
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant={alreadyConfirmed ? "outline" : "default"}
          size="sm"
          loading={confirmTracking.isPending}
          onClick={() => confirmTracking.mutate(user.userId)}
        >
          Confirm tracking is live
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsPreviewOpen(true)}
        >
          Preview email
        </Button>
        {alreadyConfirmed && (
          <span className="text-emerald-700">
            Already confirmed {alreadyConfirmed.fromNow()}
          </span>
        )}
      </div>
      {isPreviewOpen && (
        <ConfirmTrackingEmailPreviewModal
          user={user}
          onClose={() => setIsPreviewOpen(false)}
          onSendConfirm={sendAndConfirm}
          isSending={confirmTracking.isPending}
        />
      )}
    </div>
  );
}

export function UserDetailPanel({
  user,
  onClose,
  initialTab = "pipeline",
  trackingCustomerId = null,
}: {
  user: ProvisioningUser;
  onClose: () => void;
  initialTab?: Tab;
  /** When the hotel is enrolled in the tracking fleet, links the drawer to
   *  its Tracking Health page. Only passed from flag-on contexts. */
  trackingCustomerId?: string | null;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <SideModal onClose={onClose}>
      <div className="fixed z-[3] top-0 right-0 h-[100dvh] w-full max-w-[720px] bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-border-primary px-6 py-4 flex items-start justify-between z-10">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div className="font-semibold text-lg text-content-primary">
                {user.hotelName ?? "(no hotel name)"}
              </div>
              <OverallStatusBadge status={user.overallStatus} />
              <SetupModeBadge setupMode={user.setupMode} />
            </div>
            <div className="text-xs text-content-secondary">
              {user.userEmail ?? "—"} · {user.pmsType ?? "?"}
              {user.isGa4Only && " · GA4-only"}
              {trackingCustomerId && (
                <>
                  {" · "}
                  <Link
                    to={`/tracking/health/hotels/${trackingCustomerId}`}
                    className="text-brand-primary hover:text-brand-primary-hover font-medium"
                  >
                    View tracking health →
                  </Link>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-content-secondary hover:text-content-primary text-2xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Resource cards (always visible) */}
        <div className="px-6 pt-4">
          <ResourceCards user={user} />
        </div>

        {/* Self-Serve / Concierge path detail */}
        {user.setupMode && (
          <div className="px-6 pt-4">
            <PathDetail user={user} />
          </div>
        )}

        {/* Actions */}
        <div className="px-6 pt-4">
          <ActionsCard user={user} />
        </div>

        {/* Tab switcher */}
        <div className="px-6 mt-4 border-b border-border-primary">
          <div className="flex gap-4">
            <TabButton
              isActive={tab === "pipeline"}
              onClick={() => setTab("pipeline")}
            >
              Pipeline
            </TabButton>
            <TabButton
              isActive={tab === "brand-assets"}
              onClick={() => setTab("brand-assets")}
            >
              Brand Assets
            </TabButton>
            <TabButton
              isActive={tab === "competitors"}
              onClick={() => setTab("competitors")}
            >
              Competitors &amp; USP
            </TabButton>
          </div>
        </div>

        <div className="px-6 py-5">
          {tab === "pipeline" && <VerticalStepper user={user} />}
          {tab === "brand-assets" && <BrandAssets userId={user.userId} />}
          {tab === "competitors" && (
            <CompetitorStepEditor userId={user.userId} />
          )}
        </div>
      </div>
    </SideModal>
  );
}
